/**
 * S-12 «Карточка товара»: деньги за закрытую неделю (владельцу), неделя по дням, остатки,
 * реклама, отзывы и что советуют агенты — всё по одному товару. Цифры — строки сервера.
 */
import { useState } from 'react'
import { fetchSkuCard } from '../api/sections.js'
import { openExternal } from '../telegram/call.js'
import { Icon } from '../components/icons/Icon.js'
import { Spark } from '../components/charts/Charts.js'
import { Failed, Loading, NoShop, useData, useRole, useScreenHead } from './common.js'
import { Kv, Num, Proposals, Sect, ToneTag } from './ui.js'
import type { ScreenId } from '../router/screens.js'
import { useTwoCols } from '../wide.js'

type Open = (id: ScreenId, param?: string) => void

/** Фото товара; нет фото или не загрузилось — плашка с первыми буквами названия ([S-12], п. 1). */
function Photo({ src, title }: { src: string | null; title: string }): React.JSX.Element {
  const [broken, setBroken] = useState(false)
  const letters = title.replace(/[^0-9A-Za-zА-Яа-яЁё ]/g, '').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase()
  if (src === null || broken) return <span className="sku-photo sku-photo-ph" aria-hidden="true">{letters || '—'}</span>
  return <img className="sku-photo" src={src} alt="" width={72} height={96} loading="lazy" decoding="async" onError={() => setBroken(true)} />
}

export function ScreenProduct({ nm, open }: { nm: string; open: Open }): React.JSX.Element {
  const { s, reload } = useData(() => fetchSkuCard(nm))
  const role = useRole()
  const two = useTwoCols()
  const d = s.kind === 'ok' && s.data.shop !== null ? s.data : null
  useScreenHead('Товар', d === null ? null : `Артикул WB ${d.card.nm_id}`)
  if (s.kind === 'loading') return <Loading label="Загрузка товара" rows={6} />
  if (s.kind === 'fail') return <Failed message={s.message} onRetry={reload} />
  if (d === null) return <NoShop reason={s.data.reason} />
  const c = d.card
  const title = c.title ?? `Артикул WB ${c.nm_id}`
  const left = <>
      <section className="card card-pad sku-head">
        <Photo src={c.photo} title={title} />
        <div className="sku-head-main">
          <p className="sku-title">{title}</p>
          <p className="row-meta">{[c.vendor_code, c.brand, c.subject].filter(Boolean).join(' · ')}</p>
          {d.stock !== null && d.stock.availability !== null && <p className="row-meta">по оценке WB: {d.stock.availability.word.toLowerCase()}</p>}
          <button type="button" className="link-inline" data-testid="sku-wb" onClick={() => openExternal(c.wb_url)}>
            Открыть на Wildberries <Icon name="nav-external" size={16} />
          </button>
        </div>
      </section>

      {d.money !== null && <>
        <Sect title="Деньги" aside={d.money.period_display} />
        <section className="card">
          {d.money.found
            ? <Kv testid="sku-money" rows={[
                { label: 'Выручка', v: d.money.revenue },
                { label: 'К выплате от WB', v: d.money.payout, strong: true },
                { label: 'Продано', v: d.money.sold, sub: d.money.returned.display != null ? `возвратов ${d.money.returned.display}` : null },
                { label: 'Выкуп', v: d.money.buyout, sub: 'на свежей неделе занижен: товары в пути WB не учитывает' },
                { label: 'Реклама', v: d.money.ads },
                { label: 'Маржа', v: d.money.margin },
              ]} />
            : <p className="card-pad body-text muted">В закрытой неделе продаж этого товара не было.</p>}
          {d.money.calc !== null && (
            <Kv testid="sku-calc" rows={[
              { label: 'Прибыль со штуки', text: d.money.calc.profit_per_unit, strong: true, sub: `по калькулятору прибыли ${d.money.calc.since}${d.money.calc.margin_pct !== null ? ` · маржа ${d.money.calc.margin_pct}` : ''}` },
              ...(d.money.calc.week_profit !== null ? [{ label: 'Прибыль за неделю', text: d.money.calc.week_profit, sub: `${d.money.calc.week_units} шт. × прибыль со штуки` }] : []),
            ]} />
          )}
          {role === 'owner' && (
            <button type="button" className="row row-tap row-link has-ic" data-testid="sku-cost" onClick={() => open('S-27', String(c.nm_id))}>
              <Icon name="section-cost" className="ic-action" />
              <span className="row-main">
                <span className="row-title">{d.money.calc === null ? 'Посчитать прибыль со штуки' : 'Калькулятор прибыли'}</span>
                <span className="row-meta">{d.money.calc === null ? 'как в калькуляторе WB: себестоимость, расходы, налоги' : 'изменить себестоимость, расходы или налог'}</span>
              </span>
              <Icon name="nav-next" className="ic-chev row-end" />
            </button>
          )}
        </section>
      </>}

      <Sect title="Неделя" aside={d.week.period_display} />
      <section className="card">
        <div className="kpi-row">
          <span className="kpi-main">
            <span className="kpi-title">Заказы</span>
            <span className="kpi-value">{d.week.orders ?? <Num v={undefined} />}</span>
            {d.week.order_sum !== null && <span className="kpi-sub">на сумму {d.week.order_sum}</span>}
          </span>
          <span className="kpi-chart"><Spark points={d.week.points} label={d.week.summary} /></span>
        </div>
        <Kv rows={[
          { label: 'Открыли карточку', text: d.week.opens },
          { label: 'Положили в корзину', text: d.week.to_cart, sub: 'доля от открытий' },
          { label: 'Заказали из корзины', text: d.week.cart_to_order },
        ]} />
        <p className="card-foot">Показов и кликов в воронке Wildberries нет — они только в рекламной статистике.</p>
        {d.week.note !== null && <p className="card-foot">{d.week.note}</p>}
      </section>
  </>
  const right = <>
      {d.stock !== null && d.stock.places.length > 0 && <>
        <Sect title="Остатки" />
        <section className="card">
          {d.stock.places.map((p, k) => (
            <div className="row" key={k}>
              <span className="row-main">
                <span className="row-title">{p.name}{p.size !== null ? `, размер ${p.size}` : ''}</span>
                <span className="row-meta"><Num v={p.per_day} /> в день · запас <Num v={p.days_of_stock} /></span>
              </span>
              <span className="row-val"><span className="num"><Num v={p.available} /></span></span>
            </div>
          ))}
        </section>
      </>}

      {d.campaigns !== null && d.campaigns.length > 0 && <>
        <Sect title="Реклама" aside={`${d.campaigns.length}`} />
        <section className="card">
          {d.campaigns.map((cp) => (
            <button type="button" className="row row-tap" key={cp.advert_id} data-testid={'sku-campaign-' + cp.advert_id} onClick={() => open('S-14', String(cp.advert_id))}>
              <span className="row-main">
                <span className="row-title">{cp.name}</span>
                <span className="camp-tags"><ToneTag tone={cp.status.tone} word={cp.status.word} /></span>
              </span>
              <span className="row-val">
                <span className="num"><Num v={cp.spend} /></span>
                <span className="num-sub">ДРР <Num v={cp.drr} /></span>
              </span>
              <Icon name="nav-next" className="ic-chev row-end" />
            </button>
          ))}
        </section>
      </>}

      {d.feedback !== null && <>
      <Sect title="Отзывы" aside={d.feedback.rating !== null ? `оценка ${d.feedback.rating} · ${d.feedback.count}` : null} />
      <section className="card">
        {d.feedback.last.length === 0 && <p className="card-pad body-text muted">Отзывов пока нет.</p>}
        {d.feedback.last.map((f) => (
          <button type="button" className="row row-tap" key={f.id} data-testid={'sku-fb-' + f.id} onClick={() => open('S-19', f.id)}>
            <span className="row-main">
              <span className="row-meta">{f.rating !== null ? `оценка ${f.rating} · ` : ''}{f.date_display}</span>
              <span className="row-title fb-text">{f.text ?? 'Без текста — только оценка'}</span>
            </span>
            <Icon name="nav-next" className="ic-chev row-end" />
          </button>
        ))}
      </section>
      </>}

      <Proposals items={d.advice} onReport={(id) => open('S-10', id)} testid="sku-advice" />
  </>
  // Широкое окно (компьютер): слева товар, деньги и неделя, справа остатки, реклама, отзывы и советы.
  return (
    <div data-testid="screen-body">
      {two ? <div className="cols"><div className="col">{left}</div><div className="col">{right}</div></div> : <>{left}{right}</>}
    </div>
  )
}
